/**
 * DOCUMENTATION:
 * Component: AICertificationQuiz
 * Purpose: Final assessment for the AI & LLM Integration track.
 * * * * FOR JUNIOR DEVELOPERS:
 * 1. State Management: Tracks 'currentQuestion', 'score', and 'showResults'.
 * 2. Logic: Compares user selection index against the 'correctAnswer' index.
 * 3. Feedback: Provides an immediate success/fail state once the quiz ends.
 * 4. Persistence: In a real prod app, you'd save the 'isPassed' state to Firebase here.
 */

import React, { useState } from 'react';
import {
    Box, Typography, Card, Button, Stack, Radio,
    RadioGroup, FormControlLabel, FormControl, LinearProgress, styled, Paper
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PsychologyIcon from '@mui/icons-material/Psychology';

const QuizWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: "#0a0519",
    padding: theme.spacing(12, 2, 8),
    color: "white",
    display: 'flex',
    justifyContent: 'center',
}));

const GlassCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(30, 20, 50, 0.8)",
    borderRadius: "24px",
    padding: theme.spacing(6),
    border: "1px solid rgba(167, 139, 250, 0.2)",
    color: "white",
    maxWidth: "800px",
    width: "100%",
    boxShadow: "0 20px 50px rgba(0,0,0,0.5)",
}));

// --- Quiz Data ---
const questions = [
    {
        question: "What does RAG stand for in the context of LLMs?",
        options: [
            "Random Access Generation",
            "Retrieval-Augmented Generation",
            "Rapid AI Guidance",
            "Redundant Analysis Group"
        ],
        correctAnswer: 1,
    },
    {
        question: "What is the primary purpose of an 'Embedding' model like embedding-001?",
        options: [
            "To compress text files for storage",
            "To translate English to Python code",
            "To convert text into numerical vectors for semantic search",
            "To generate images from text descriptions"
        ],
        correctAnswer: 2,
    },
    {
        question: "In our ProDev architecture, where is the Vector Store stored to maintain the Free Tier?",
        options: [
            "In a paid Pinecone Database",
            "In the browser's RAM (Local Memory)",
            "In a Firebase Realtime Database",
            "On a physical hard drive in the cloud"
        ],
        correctAnswer: 1,
    },
    {
        question: "Which Gemini parameter controls the 'creativity' or randomness of the output?",
        options: [
            "Top-K",
            "Max Output Tokens",
            "Safety Settings",
            "Temperature"
        ],
        correctAnswer: 3,
    },
    {
        question: "Why do we use 'Streaming' responses for the AI Mentor?",
        options: [
            "To bypass Google's API costs",
            "To reduce the perceived latency for the user",
            "To ensure the answer is more accurate",
            "Because batching isn't supported by React"
        ],
        correctAnswer: 1,
    }
];

export default function AICertificationQuiz() {
    const navigate = useNavigate();
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [selectedOption, setSelectedOption] = useState(null);
    const [score, setScore] = useState(0);
    const [showResults, setShowResults] = useState(false);

    const handleNext = () => {
        if (selectedOption === questions[currentQuestion].correctAnswer) {
            setScore(prev => prev + 1);
        }

        if (currentQuestion + 1 < questions.length) {
            setCurrentQuestion(prev => prev + 1);
            setSelectedOption(null);
        } else {
            setShowResults(true);
        }
    };

    const percentage = (score / questions.length) * 100;
    const isPassed = percentage >= 80;

    if (showResults) {
        return (
            <QuizWrapper>
                <GlassCard sx={{ textAlign: 'center' }}>
                    <EmojiEventsIcon sx={{ fontSize: 80, color: isPassed ? '#FBBC04' : '#666', mb: 2 }} />
                    <Typography variant="h3" fontWeight={900} gutterBottom>
                        {isPassed ? "CERTIFIED!" : "KEEP LEARNING"}
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 4, opacity: 0.8 }}>
                        You scored {score} out of {questions.length} ({percentage}%)
                    </Typography>

                    <Typography variant="body1" sx={{ mb: 4, opacity: 0.6 }}>
                        {isPassed
                            ? "Congratulations Architect! You've mastered the integration of RAG and Gemini. Your badge has been added to your profile."
                            : "You need 80% to pass. Review the RAG and Vector Database lessons and try again!"}
                    </Typography>

                    <Stack direction="row" spacing={2} justifyContent="center">
                        <Button variant="outlined" color="inherit" onClick={() => window.location.reload()}>
                            Retake Quiz
                        </Button>
                        <Button
                            variant="contained"
                            sx={{ bgcolor: '#a78bfa', color: '#0a0519', fontWeight: 800 }}
                            onClick={() => navigate('/ai-dashboard')}
                        >
                            Back to Dashboard
                        </Button>
                    </Stack>
                </GlassCard>
            </QuizWrapper>
        );
    }

    return (
        <QuizWrapper>
            <GlassCard>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
                    <Box>
                        <Typography variant="h6" fontWeight={800} color="#a78bfa">
                            AI Certification Assessment
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.5 }}>
                            Question {currentQuestion + 1} of {questions.length}
                        </Typography>
                    </Box>
                    <PsychologyIcon sx={{ color: 'rgba(167, 139, 250, 0.4)' }} />
                </Stack>

                <LinearProgress
                    variant="determinate"
                    value={((currentQuestion) / questions.length) * 100}
                    sx={{ mb: 4, borderRadius: 5, height: 8, bgcolor: 'rgba(255,255,255,0.05)', '& .MuiLinearProgress-bar': { bgcolor: '#a78bfa' }}}
                />

                <Typography variant="h5" fontWeight={600} sx={{ mb: 4 }}>
                    {questions[currentQuestion].question}
                </Typography>

                <FormControl component="fieldset" sx={{ width: '100%', mb: 4 }}>
                    <RadioGroup
                        value={selectedOption}
                        onChange={(e) => setSelectedOption(parseInt(e.target.value))}
                    >
                        {questions[currentQuestion].options.map((option, index) => (
                            <Paper
                                key={index}
                                elevation={0}
                                sx={{
                                    mb: 2,
                                    p: 1,
                                    bgcolor: selectedOption === index ? 'rgba(167, 139, 250, 0.15)' : 'rgba(255,255,255,0.03)',
                                    border: `1px solid ${selectedOption === index ? '#a78bfa' : 'rgba(255,255,255,0.1)'}`,
                                    borderRadius: '12px',
                                    transition: '0.2s',
                                    '&:hover': { bgcolor: 'rgba(167, 139, 250, 0.05)' }
                                }}
                            >
                                <FormControlLabel
                                    value={index}
                                    control={<Radio sx={{ color: '#a78bfa', '&.Mui-checked': { color: '#a78bfa' } }} />}
                                    label={option}
                                    sx={{ width: '100%', m: 0, color: 'white' }}
                                />
                            </Paper>
                        ))}
                    </RadioGroup>
                </FormControl>

                <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={selectedOption === null}
                    onClick={handleNext}
                    sx={{
                        bgcolor: '#a78bfa',
                        color: '#0a0519',
                        fontWeight: 800,
                        py: 1.5,
                        '&:hover': { bgcolor: '#9061f9' }
                    }}
                >
                    {currentQuestion + 1 === questions.length ? "Finish Assessment" : "Next Question"}
                </Button>
            </GlassCard>
        </QuizWrapper>
    );
}
